/**
 * Embarques en curso — widget del Inicio: contenedores de importación vivos
 * con su semáforo de fechas (fabricación agotándose, saldo a ETA-20,
 * manifestación a ETA-5, liquidación y recepción pendientes).
 *
 * El Inicio lo monta para roles con el programa "importaciones" (misma key
 * que routes.js). Reutiliza alertaEmbarque del listado para que el
 * semáforo sea el mismo en ambos lados. Los embarques con alerta van
 * primero (rojo, ámbar, verde) y el resto por id descendente. Clic en una
 * fila abre el expediente del embarque dentro del listado (?id=).
 * Con datos en cero muestra estado vacío (visible siempre que haya permiso,
 * igual que Cobros de Hoy).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { useApi } from "lib/api";
import { FP } from "components/financepro/tokens";
import { alertaEmbarque } from "modules/importaciones/embarques";

const MAX_FILAS = 6;
const ESTADOS_VIVOS = ["negociacion", "fabricacion", "transito", "puerto", "liquidacion"];
const ETIQUETA_ESTADO = {
  negociacion: "Negociación",
  fabricacion: "Fabricación",
  transito: "En tránsito",
  puerto: "En puerto",
  liquidacion: "Liquidado",
};
const PESO_NIVEL = { rojo: 0, ambar: 1, verde: 2 };
const COLOR_NIVEL = {
  rojo: { bg: "#FFEBEE", fg: "#C62828" },
  ambar: { bg: "#FFF3E0", fg: "#E65100" },
  verde: { bg: "#E8F5E9", fg: "#2E7D32" },
};

function Stat({ valor, etiqueta }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: FP.color.textPrimary }}>
        {valor}
      </Typography>
      <Typography sx={{ fontSize: "0.74rem", color: FP.color.textSecondary }}>
        {etiqueta}
      </Typography>
    </Box>
  );
}

function EmbarquesEnCurso() {
  const navigate = useNavigate();
  const { post } = useApi();
  const [embarques, setEmbarques] = useState(null);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    let activo = true;
    post("/api/importaciones_listar", {}).then(({ ok, data }) => {
      if (activo && ok && Array.isArray(data.embarques)) setEmbarques(data.embarques);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!embarques) return null;

  const vivos = embarques
    .filter((e) => ESTADOS_VIVOS.includes(e.estado))
    .map((e) => ({ ...e, alerta: alertaEmbarque(e) }))
    .sort((a, b) => {
      const pa = a.alerta ? PESO_NIVEL[a.alerta.nivel] : 9;
      const pb = b.alerta ? PESO_NIVEL[b.alerta.nivel] : 9;
      return pa - pb || b.id - a.id;
    });
  const visibles = expandido ? vivos : vivos.slice(0, MAX_FILAS);
  const conAlertaRoja = vivos.filter((e) => e.alerta?.nivel === "rojo").length;
  const chasisPorRecibir = vivos.reduce(
    (s, e) => s + Math.max(0, (e.chasis_total || 0) - (e.chasis_recibidos || 0)),
    0
  );

  const abrir = (id) => navigate(`/importaciones/embarques?id=${id}`);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.8rem",
          letterSpacing: "0.08em",
          color: FP.color.textSecondary,
          mb: 1.2,
        }}
      >
        EMBARQUES EN CURSO
      </Typography>
      <Card
        elevation={0}
        sx={{ borderRadius: 2.5, border: "1px solid #E5E9F0", boxShadow: FP.shadow.soft }}
      >
        <Box sx={{ display: "flex", gap: { xs: 3, sm: 5 }, flexWrap: "wrap", p: 2, pb: 1.4 }}>
          <Stat valor={vivos.length} etiqueta="Embarques abiertos" />
          <Stat valor={conAlertaRoja} etiqueta="Con alerta urgente" />
          <Stat valor={chasisPorRecibir} etiqueta="Chasis por recibir" />
        </Box>

        {visibles.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1.6,
              borderTop: "1px solid #EEF1F6",
            }}
          >
            <Icon sx={{ fontSize: 20, color: FP.color.textSecondary }}>directions_boat</Icon>
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary }}>
              No hay embarques en curso.
            </Typography>
          </Box>
        )}

        {visibles.length > 0 && (
          <Box sx={{ px: 1, pb: 1, maxHeight: expandido ? 420 : "none", overflowY: "auto" }}>
            {visibles.map((e) => (
              <Box
                key={e.id}
                onClick={() => abrir(e.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.4,
                  px: 1,
                  py: 1.1,
                  borderTop: "1px solid #EEF1F6",
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": { bgcolor: `${FP.color.navy}08` },
                }}
              >
                <Icon sx={{ fontSize: 20, color: FP.color.navy, flexShrink: 0 }}>
                  directions_boat
                </Icon>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{ fontWeight: 700, fontSize: "0.88rem", color: FP.color.textPrimary }}
                  >
                    {String(e.suplidornombre || "").trim() || `Embarque #${e.id}`}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.74rem", color: FP.color.textSecondary }}>
                    Embarque #{e.id} · {ETIQUETA_ESTADO[e.estado] || e.estado}
                    {e.chasis_total ? ` · ${e.chasis_total} chasis` : ""}
                    {e.fechaeta ? ` · ETA ${e.fechaeta}` : ""}
                  </Typography>
                </Box>
                {e.alerta && (
                  <Chip
                    size="small"
                    label={e.alerta.label}
                    sx={{
                      bgcolor: COLOR_NIVEL[e.alerta.nivel].bg,
                      color: COLOR_NIVEL[e.alerta.nivel].fg,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", borderTop: "1px solid #EEF1F6" }}>
          {vivos.length > MAX_FILAS && (
            <Box
              onClick={() => setExpandido(!expandido)}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.6,
                py: 1.1,
                borderRight: "1px solid #EEF1F6",
                cursor: "pointer",
                color: FP.color.navy,
                "&:hover": { bgcolor: `${FP.color.navy}08` },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                {expandido ? "Mostrar menos" : `Ver los ${vivos.length} embarques`}
              </Typography>
              <Icon sx={{ fontSize: 16 }}>{expandido ? "expand_less" : "expand_more"}</Icon>
            </Box>
          )}
          <Box
            onClick={() => navigate("/importaciones/embarques")}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              py: 1.1,
              cursor: "pointer",
              color: FP.color.navy,
              "&:hover": { bgcolor: `${FP.color.navy}08` },
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>Ir a Embarques</Typography>
            <Icon sx={{ fontSize: 16 }}>arrow_forward</Icon>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

export default EmbarquesEnCurso;
